/**
 * AiSupportPanel — CC-2d read-only support assistant (ТЗ §13 AI Support
 * Assistant, sub-ai-support — AI row 27).
 *
 * Q&A surface over the `AiSupportProvider` seam. The provider receives the
 * question + a READ-ONLY session snapshot (recent command history, last
 * submitted command, selection metadata, active space — ТЗ §13) and returns
 * localized answer text. There is NO dispatch path anywhere in this
 * component besides the `ui/*` telemetry ring (§14 "использование AI"),
 * which never touches geometry, the visible audit ring or history — the
 * support channel is non-mutating by construction.
 */

import { useState, type FormEvent, type ReactElement } from "react";

import {
  localAiSupportProvider,
  type AiSupportContext,
} from "../../ai/aiSupportAssistant";
import {
  selectSelectedEntities,
  useEditorStore,
} from "../../state/editorStore";
import { useLanguage, useT } from "../../i18n";
import { useAiPanelTranscriptStore } from "./aiPanelTranscript";

/**
 * Project the READ-ONLY support context out of the shell state. Plain data
 * snapshot — the provider never sees the store itself.
 */
function buildAiSupportContext(): AiSupportContext {
  const state = useEditorStore.getState().state;
  const selectedEntities = selectSelectedEntities(state);
  return {
    recentCommands: state.audit.events
      .filter((event) => !event.commandType.startsWith("ui/"))
      .map((event) => event.commandType),
    lastSubmittedCommand: state.commandLine.lastSubmitted,
    selectionCount: selectedEntities.length,
    selectionTypes: [
      ...new Set(selectedEntities.map((entity) => entity.type)),
    ],
    activeSpaceId: state.drawing.activeSpaceId,
  };
}

export function AiSupportPanel(): ReactElement {
  const t = useT();
  const { language } = useLanguage();
  const recordUiClick = useEditorStore((store) => store.recordUiClick);
  const supportEntries = useAiPanelTranscriptStore(
    (store) => store.supportEntries,
  );
  const appendSupportEntry = useAiPanelTranscriptStore(
    (store) => store.appendSupportEntry,
  );
  const [query, setQuery] = useState("");

  function submitQuery(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const question = query.trim();
    if (question.length === 0) return;

    const answer = localAiSupportProvider.answer(
      question,
      buildAiSupportContext(),
      language,
    );
    appendSupportEntry({ question, paragraphs: answer.paragraphs });
    // §14 telemetry — AI usage. Rides the separate ui/* telemetry queue
    // only: zero visible audit events, zero geometry writes (read-only
    // contract of the support channel).
    recordUiClick({
      target: "ai-support",
      controlId: "ask",
      summary: `AI support query (${
        answer.isFallback ? "fallback" : answer.matchedTopics.join(", ")
      })`,
    });
    setQuery("");
  }

  return (
    <div className="section-subcard" data-testid="ai-support-panel">
      <div className="section-heading">
        <div>
          <p className="section-label">{t("ai.support.section_label")}</p>
          <h3>{t("ai.support.title")}</h3>
        </div>
        <span className="badge">{t("ai.support.badge")}</span>
      </div>
      <p className="panel-note">{t("ai.support.intro")}</p>

      <form
        className="panel-stack"
        data-testid="ai-support-form"
        onSubmit={submitQuery}
      >
        <label className="field-stack">
          <span>{t("ai.support.query_label")}</span>
          <input
            className="property-input"
            data-testid="ai-support-query"
            value={query}
            placeholder={t("ai.support.query_placeholder")}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="action-row">
          <button
            className="primary-button"
            type="submit"
            data-testid="ai-support-submit"
            disabled={query.trim().length === 0}
          >
            {t("ai.support.submit")}
          </button>
          <span className="microcopy">{t("ai.support.readonly_hint")}</span>
        </div>
      </form>

      <div className="history-list" data-testid="ai-support-transcript">
        {supportEntries.length === 0 ? (
          <p className="panel-note">{t("ai.support.empty")}</p>
        ) : (
          supportEntries.map((entry, index) => (
            <article key={entry.id} className="history-card tone-info">
              <strong>{entry.question}</strong>
              {/* The LATEST answer carries the stable testid (entries are
                  latest-first) so e2e/unit specs always read the newest. */}
              <div
                {...(index === 0
                  ? { "data-testid": "ai-support-answer" }
                  : {})}
              >
                {entry.paragraphs.map((paragraph, paragraphIndex) => (
                  <p key={`${entry.id}-${paragraphIndex}`}>{paragraph}</p>
                ))}
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
